import { tx } from "@/i18n/tx";
import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { api, formatError } from "@/lib/api";
import { localizeAsset } from "@/lib/assets";
import { useAuth } from "@/context/AuthContext";
import { useTranslation } from "react-i18next";
import { AppShell, Loader } from "@/components/AppShell";
import { ShareSheet } from "@/components/ShareSheet";
import { getPublicBase } from "@/lib/share";
import { toast } from "sonner";
import { Mic, Square, Sparkles, Flame, Lock, LifeBuoy, Mail, Share2 } from "lucide-react";
import { DailyAtmosphere } from "@/components/DailyAtmosphere";

function greeting(t) {
  const h = new Date().getHours();
  if (h < 6) return t("home.gNight");
  if (h < 12) return t("home.gMorning");
  if (h < 18) return t("home.gAfternoon");
  return t("home.gEvening");
}

export default function Home() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [symbols, setSymbols] = useState([]);
  const [response, setResponse] = useState("");
  const [busy, setBusy] = useState(false);
  const [reflection, setReflection] = useState(null);
  const [crisis, setCrisis] = useState(null);
  const [daySymbol, setDaySymbol] = useState(null);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [sttError, setSttError] = useState("");
  const lastAudioRef = useRef(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [publicBase, setPublicBase] = useState("");
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);

  const load = async () => {
    try {
      const [d, s, ds] = await Promise.all([api.get("/daily"), api.get("/symbols"), api.get("/symbols/daily")]);
      setData(d.data);
      setSymbols(s.data);
      setDaySymbol(ds.data);
      if (d.data.entry?.ai_response) setReflection(d.data.entry.ai_response);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    }
  };
  // Caricamento iniziale una sola volta al mount; `load` usa solo riferimenti stabili.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); getPublicBase().then(setPublicBase); }, []);

  const submit = async () => {
    if (response.trim().length < 2) { toast.error(t("home.writeFirst")); return; }
    setBusy(true);
    try {
      const { data: res } = await api.post("/daily/respond", { response });
      if (res.crisis) { setCrisis(res.message); }
      else { setReflection(res.entry.ai_response); load(); }
    } catch (err) {
      const detail = formatError(err.response?.data?.detail);
      if (err.response?.status === 402) toast.error(detail);
      else toast.error(detail);
    } finally { setBusy(false); }
  };

  const toggleRecord = async () => {
    if (recording) {
      mediaRef.current?.stop();
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => chunksRef.current.push(e.data);
      mr.onstop = async () => {
        stream.getTracks().forEach((tr) => tr.stop());
        setRecording(false);
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        sendAudio(blob);
      };
      mediaRef.current = mr;
      mr.start();
      setRecording(true);
    } catch {
      toast.error(t("home.micUnavailable"));
    }
  };

  const sendAudio = async (blob) => {
    lastAudioRef.current = blob;
    setSttError("");
    setTranscribing(true);
    const fd = new FormData();
    fd.append("file", blob, "audio.webm");
    try {
      const { data: r } = await api.post("/voice/transcribe", fd, { timeout: 60000 });
      setResponse((prev) => (prev ? prev + " " : "") + (r.text || ""));
      lastAudioRef.current = null;
    } catch (err) {
      const timedOut = err.code === "ECONNABORTED" || /timeout/i.test(err.message || "");
      setSttError(timedOut ? t("home.transcribeTimeout") : (formatError(err.response?.data?.detail) || t("home.transcribeFail")));
    } finally {
      setTranscribing(false);
    }
  };

  if (!data) return <Loader />;
  const name = data.greeting_name;

  const shareUrl = publicBase || window.location.origin;
  const shareOptions = [];
  if (data.question?.text) shareOptions.push({ id: "question", label: t("share.labelQuestion"), text: data.question.text });
  if (reflection) {
    const r = [reflection.emerges, reflection.reading, reflection.deeper_question, reflection.step].filter(Boolean).join("\n\n");
    if (r) shareOptions.push({ id: "reflection", label: t("share.labelReflection"), text: r });
  }
  if (daySymbol?.name) shareOptions.push({ id: "symbol", label: t("share.labelSymbol"), text: `${daySymbol.name}${daySymbol.phrase ? " — " + daySymbol.phrase : ""}` });

  return (
    <AppShell>
      <div className="px-7 pt-8">
        <div className="mb-7">
          <DailyAtmosphere />
        </div>
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
          <p className="font-serif text-lg text-anima-muted mb-3 tracking-wide">{tx("Ascolta Anima")}</p>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-anima-muted text-sm">{greeting(t)},</p>
              <h1 className="font-serif text-4xl text-anima-ink leading-tight">{name || t("home.defaultName")}</h1>
            </div>
            <div className="flex items-center gap-2">
              {data.streak > 0 && (
                <div className="flex items-center gap-1.5 rounded-full bg-anima-gold/25 px-3 py-1.5" title={tx("Continuita del percorso")}>
                  <Flame size={15} className="text-anima-golddeep" />
                  <span className="text-sm text-anima-ink">{data.streak}</span>
                </div>
              )}
              <button
                onClick={() => setShareOpen(true)}
                data-testid="home-share-btn"
                aria-label={t("share.action")}
                className="w-10 h-10 rounded-full flex items-center justify-center border border-anima-ink/10 bg-white/55 text-anima-muted active:scale-95 transition-transform duration-200"
              >
                <Share2 size={17} />
              </button>
            </div>
          </div>
        </motion.div>

        {user && user.email_verified === false && (
          <div data-testid="verify-banner" className="mt-6 anima-card rounded-2xl p-4 flex items-center gap-3">
            <Mail size={18} className="text-anima-golddeep shrink-0" />
            <div className="flex-1">
              <p className="text-sm text-anima-ink font-medium">{t("home.verifyTitle")}</p>
              <p className="text-xs text-anima-muted">{t("home.verifyBody")}</p>
            </div>
            <button
              data-testid="verify-resend-btn"
              onClick={async () => { try { await api.post("/auth/resend-verification"); toast.success(t("home.verifySent")); } catch (e) { toast.error(t("home.verifyRetry")); } }}
              className="text-xs text-anima-sagedeep underline shrink-0"
            >
              {t("home.verifyResend")}
            </button>
          </div>
        )}

        {!data.has_access && (
          <div className="mt-6 anima-card rounded-2xl p-5" data-testid="home-upgrade-banner">
            <div className="flex items-center gap-2 mb-1.5">
              <Lock size={18} className="text-anima-golddeep shrink-0" />
              <p className="text-sm text-anima-ink font-medium">{t("home.trialTitle")}</p>
            </div>
            <p className="text-xs text-anima-muted mb-3">{t("home.trialBody")}</p>
            <div className="flex gap-2 flex-wrap">
              <Link to="/checkout/anima_monthly" data-testid="home-choose-monthly" className="rounded-full bg-anima-sagedeep text-anima-ivory px-5 py-2.5 text-sm">{t("home.chooseMonthly")}</Link>
              <Link to="/checkout/anima_yearly" data-testid="home-choose-yearly" className="rounded-full bg-anima-golddeep text-anima-ivory px-5 py-2.5 text-sm">{t("home.chooseYearly")}</Link>
            </div>
          </div>
        )}

        <AnimatePresence mode="wait">
          {!reflection ? (
            <motion.div key="write" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              {daySymbol && (
                <Link to={`/app/simboli/${daySymbol.key}`} data-testid="home-daily-symbol" className="mt-8 flex items-center gap-4 active:scale-[0.98] transition-transform duration-200">
                  <img src={localizeAsset(daySymbol.image_url)} alt={daySymbol.name} className="w-16 h-16 rounded-2xl object-cover anima-float" />
                  <div>
                    <p className="text-[11px] uppercase tracking-widest text-anima-muted">{t("home.dailySymbol")}</p>
                    <p data-testid="home-daily-symbol-name" className="font-serif text-xl text-anima-ink">{daySymbol.name}</p>
                  </div>
                </Link>
              )}

              <div className="mt-8 rounded-3xl bg-anima-pinklight/80 border border-anima-ink/5 p-6">
                <p className="text-[11px] uppercase tracking-widest text-anima-muted mb-3">{t("home.todayQuestion")}</p>
                <p data-testid="daily-question" className="font-serif text-[26px] leading-snug text-anima-ink">{data.question?.text}</p>
              </div>

              <div className="mt-6 rounded-3xl bg-white/50 border border-anima-ink/5 p-5">
                <textarea
                  data-testid="daily-response-input"
                  className="anima-write text-xl text-anima-ink min-h-[140px]"
                  placeholder={t("home.writePlaceholder")}
                  value={response}
                  onChange={(e) => setResponse(e.target.value)}
                />
                <div className="flex items-center justify-between pt-3 border-t border-anima-ink/5">
                  <button
                    data-testid="voice-record-btn" onClick={toggleRecord} disabled={transcribing}
                    className={`flex items-center gap-2 text-sm rounded-full px-4 py-2 transition-colors duration-300 disabled:opacity-50 ${recording ? "bg-anima-pink text-anima-ink anima-pulse" : "text-anima-muted"}`}
                  >
                    {recording ? <Square size={15} /> : <Mic size={16} />}
                    {recording ? t("home.recordStop") : t("home.recordStart")}
                  </button>
                  <span className="text-xs text-anima-muted">{t("home.chars", { n: response.length })}</span>
                </div>
                {transcribing && (
                  <div data-testid="transcribing-indicator" role="status" aria-live="polite" className="mt-3 flex items-center gap-3 text-sm text-anima-muted">
                    <span className="flex gap-1" aria-hidden="true">
                      {[0, 1, 2].map((i) => (
                        <motion.span key={i} className="w-1.5 h-1.5 rounded-full bg-anima-sagedeep"
                          animate={{ opacity: [0.25, 1, 0.25] }} transition={{ duration: 1.2, repeat: Infinity, delay: i * 0.2 }} />
                      ))}
                    </span>
                    {t("home.transcribingLong")}
                  </div>
                )}
                {sttError && !transcribing && (
                  <div data-testid="transcribe-error" role="alert" className="mt-3 flex items-center justify-between gap-3 text-sm text-anima-ink/85">
                    <span>{sttError}</span>
                    {lastAudioRef.current && (
                      <button data-testid="transcribe-retry-btn" onClick={() => sendAudio(lastAudioRef.current)}
                        className="shrink-0 rounded-full border border-anima-ink/15 px-4 py-1.5 text-sm text-anima-ink">
                        {t("home.transcribeRetry")}
                      </button>
                    )}
                  </div>
                )}
              </div>

              <button
                data-testid="listen-response-btn" onClick={submit} disabled={busy || transcribing || recording}
                className="mt-6 w-full rounded-full bg-anima-sagedeep text-anima-ivory py-4 font-medium active:scale-95 transition-transform duration-200 disabled:opacity-60 flex items-center justify-center gap-2"
              >
                <Sparkles size={17} strokeWidth={1.6} />
                {busy ? t("home.listening") : t("home.listen")}
              </button>
            </motion.div>
          ) : (
            <Reflection key="reflect" reflection={reflection} question={data.question} response={data.entry?.user_response || response} onReset={() => { setReflection(null); setResponse(""); }} answeredToday={data.answered_today} t={t} />
          )}
        </AnimatePresence>
      </div>

      {shareOpen && <ShareSheet options={shareOptions} url={shareUrl} onClose={() => setShareOpen(false)} />}
      {crisis && <CrisisModal message={crisis} onClose={() => setCrisis(null)} t={t} />}
    </AppShell>
  );
}

function Section({ label, children, delay }) {
  return (
    <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay, duration: 0.6 }}>
      <p className="text-[11px] uppercase tracking-widest text-anima-golddeep mb-1.5">{label}</p>
      <div className="text-anima-ink">{children}</div>
    </motion.div>
  );
}

function Reflection({ reflection, question, response, onReset, answeredToday, t }) {
  return (
    <div className="mt-8 space-y-7 pb-4">
      <div className="rounded-3xl bg-anima-pinklight/70 border border-anima-ink/5 p-5">
        <p className="text-[11px] uppercase tracking-widest text-anima-muted mb-2">{t("home.yourResponse")}</p>
        <p className="font-serif text-lg text-anima-ink italic leading-relaxed">"{response}"</p>
      </div>

      <Section label={t("home.secEmerges")} delay={0.05}>
        <p className="text-lg leading-relaxed font-serif">{reflection.emerges}</p>
      </Section>
      <Section label={t("home.secReading")} delay={0.15}>
        <p className="text-base leading-relaxed">{reflection.reading}</p>
      </Section>

      <Section label={t("home.secSymbol")} delay={0.25}>
        <div className="rounded-3xl overflow-hidden bg-white/50 border border-anima-ink/5">
          {reflection.symbol_image && (
            <img src={localizeAsset(reflection.symbol_image)} alt={reflection.symbol_name} className="w-full h-52 object-cover" />
          )}
          <div className="p-4">
            <p className="font-serif text-2xl text-anima-ink">{reflection.symbol_name}</p>
            <p className="text-sm text-anima-muted mt-1">{reflection.symbol_meaning}</p>
            <p className="text-[12px] text-anima-muted/80 italic mt-2">{t("home.symbolHint")}</p>
            {reflection.symbol_is_artwork && (
              <div className="mt-2">
                <p className="text-[11px] text-anima-muted">{reflection.symbol_credit || t("home.ianasCredit")}</p>
                <Link to="/ianas" data-testid="reflection-ianas-link" className="text-[12px] text-anima-sagedeep underline">{t("home.ianasLink")}</Link>
              </div>
            )}
          </div>
        </div>
      </Section>

      <Section label={t("home.secDeeper")} delay={0.35}>
        <p className="text-xl leading-snug font-serif text-anima-ink">{reflection.deeper_question}</p>
      </Section>

      <Section label={t("home.secStep")} delay={0.45}>
        <div className="rounded-3xl bg-anima-sage/20 border border-anima-sage/30 p-5">
          <p className="text-base leading-relaxed text-anima-ink">{reflection.step}</p>
        </div>
      </Section>

      <div className="flex gap-3 pt-2">
        <Link to="/app/diario" data-testid="reflection-diario-btn" className="flex-1 rounded-full border border-anima-ink/15 text-anima-ink py-3.5 text-center text-sm">
          {t("home.toDiary")}
        </Link>
        {!answeredToday && (
          <button onClick={onReset} className="rounded-full border border-anima-ink/15 text-anima-muted py-3.5 px-5 text-sm">
            {t("home.rewrite")}
          </button>
        )}
      </div>
    </div>
  );
}

function CrisisModal({ message, onClose, t }) {
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-anima-ink/30 backdrop-blur-sm px-4 py-6">
      <motion.div
        initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md bg-anima-ivory rounded-3xl p-7 border border-anima-ink/10"
        data-testid="crisis-modal"
      >
        <div className="w-12 h-12 rounded-full bg-anima-pink flex items-center justify-center mb-4">
          <LifeBuoy size={22} className="text-anima-ink" />
        </div>
        <h3 className="font-serif text-2xl text-anima-ink mb-3">{message.title}</h3>
        <p className="text-anima-ink/90 leading-relaxed whitespace-pre-line text-[15px]">{message.body}</p>
        <a href="tel:112" className="mt-5 block w-full rounded-full bg-anima-sagedeep text-anima-ivory py-4 text-center font-medium">
          {t("home.crisisCall")}
        </a>
        <button onClick={onClose} data-testid="crisis-close-btn" className="mt-3 w-full text-anima-muted py-2 text-sm">
          {t("home.crisisClose")}
        </button>
      </motion.div>
    </div>
  );
}
